import { CornerDownRight } from 'lucide-react'
import { useRef } from 'react'
import { CitationList } from '@/components/CitationList'
import {
  ResponseHighlight,
  textNamesBrand,
} from '@/components/ResponseHighlight'
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import type { EngineAnswerProps } from './EngineAnswer.types'

// Scroll the first brand mention in THIS answer into view and flash it.
function jumpToMention(body: HTMLElement | null) {
  const first = body?.querySelector<HTMLElement>('mark[data-brand]')
  if (!first) return

  first.scrollIntoView({ behavior: 'smooth', block: 'center' })

  first.classList.add('ring-2', 'ring-primary', 'ring-offset-2')
  window.setTimeout(() => {
    first.classList.remove('ring-2', 'ring-primary', 'ring-offset-2')
  }, 1400)
}

// One engine's answer, collapsed by default so a query does not open as a wall of text.
export function EngineAnswer({
  response,
  brand,
  competitors,
  meta,
}: EngineAnswerProps) {
  const body = useRef<HTMLDivElement>(null)

  const canJump = !response.error && textNamesBrand(response.answer, brand)

  return (
    <AccordionItem value={response.engineId} className="border-b-0">
      <AccordionTrigger className="px-1 hover:no-underline">
        <span className="flex w-full items-center gap-2 pr-2 text-left">
          <span className="font-semibold text-sm">{response.label}</span>
          {meta}
        </span>
      </AccordionTrigger>

      <AccordionContent className="pb-3">
        {response.error ? (
          <p className="text-sm text-destructive">n/a — {response.error}</p>
        ) : (
          <div
            ref={body}
            className="rounded-md border border-border bg-background/40 p-3"
          >
            {canJump && (
              <button
                type="button"
                onClick={() => jumpToMention(body.current)}
                className="mb-2 inline-flex cursor-pointer items-center gap-1 rounded-full border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary transition hover:bg-primary/15"
              >
                <CornerDownRight className="size-3" />
                Jump to {brand}
              </button>
            )}

            <ResponseHighlight
              text={response.answer}
              brand={brand}
              competitors={competitors}
            />

            {response.truncated && (
              <div className="mt-2 text-xs text-amber-600 dark:text-amber-500">
                Answer cut short — the model was still searching.
              </div>
            )}

            <CitationList
              citations={response.citations}
              references={response.references}
              status={response.citationStatus}
            />
          </div>
        )}
      </AccordionContent>
    </AccordionItem>
  )
}
